import {v4 as uuidv4} from 'uuid';
import {getFirebaseToken} from '../components/common/firebase';
import {request, BACKEND_BASE_URL} from '../services/apiClient';

const DEFAULT_BACKEND_URL = BACKEND_BASE_URL;
const DEFAULT_LIVEKIT_SERVER_URL =
  process.env.REACT_APP_LIVEKIT_SERVER_URL ||
  'wss://calliope-jn2ckdv1.livekit.cloud';

const HARD_CODED_SERVER_URL =
  process.env.REACT_APP_LIVEKIT_SERVER_OVERRIDE || null;
const HARD_CODED_TOKEN =
  process.env.REACT_APP_LIVEKIT_PARTICIPANT_TOKEN || null;

function unwrapLiveKitResponse(payload: any) {
  if (payload?.data && typeof payload.data === 'object') {
    return payload.data;
  }
  return payload || {};
}

async function fetchConnectionDetailsFromBackend({
  backendUrl,
  roomName,
  participantName,
  language,
  dialect,
  authToken,
  patientId,
  patientName,
  ehrPatientId,
  patientGender,
  providerName,
  encounterId,
  simultaneous,
  isTelephonic,
}: Record<string, any>) {
  if (!backendUrl) {
    return null;
  }

  const baseUrl = backendUrl.replace(/\/?$/, '');

  // New session → unified POST /v1/interpreter/sessions with a `web` device.
  // Refresh / reconnect into an existing room → legacy POST /livekit/token
  // (re-mints a token for the same room + identity, which the unified
  // device-attach API does not do).
  const path = roomName ? '/livekit/token' : '/v1/interpreter/sessions';
  const payload = roomName
    ? {roomName, participantName, language, dialect}
    : {
        deviceKind: 'web',
        participantName,
        language,
        dialect,
        // Opt-in low-latency interpretation (gpt-realtime-translate). Only sent
        // on new sessions; omitted (falsy) leaves the default cascaded
        // interpreter untouched.
        ...(simultaneous ? {simultaneous: true} : {}),
        context: {
          patientId: patientId || undefined,
          patientName: patientName || undefined,
          ehrPatientId: ehrPatientId || undefined,
          patientGender: patientGender || undefined,
          providerName: providerName || undefined,
          encounterId: encounterId || undefined,
          // Web dial-out encounter: the patient is reached by phone (SIP leg),
          // so this is a telephonic visit. The backend stamps `is_telephonic`
          // into room metadata so the agent records the Conversation as a
          // Phone visit even when the client's own doc write never lands
          // (e.g. browser crash mid-call). Only sent when true.
          ...(isTelephonic ? {isTelephonic: true} : {}),
        },
      };

  // Auth is optional for LiveKit token issuance: attach the token only when
  // the caller supplied one (auth:false stops apiClient from fetching one).
  // apiClient.request throws an ApiError carrying `.status`, which the 404
  // legacy-fallback branch below relies on.
  const response = await request(path, {
    method: 'POST',
    baseUrl,
    auth: false,
    authToken,
    unwrap: false,
    successCheck: false,
    body: JSON.stringify(payload),
  });
  const data = unwrapLiveKitResponse(response);
  const token = data.token || data.participantToken;

  if (!token) {
    throw new Error('LiveKit token response missing token field');
  }

  const resolvedRoomName = data.roomName || data.room_name || roomName;
  const resolvedSessionId =
    data.sessionId ||
    data.session_id ||
    data.conversationId ||
    data.conversation_id ||
    data.id ||
    resolvedRoomName ||
    null;

  return {
    serverUrl: data.serverUrl || data.server_url || DEFAULT_LIVEKIT_SERVER_URL,
    roomName: resolvedRoomName,
    participantName:
      data.participantName || data.participant_name || participantName,
    participantToken: token,
    sessionId: resolvedSessionId,
  };
}

function fetchHardCodedConnection({
  roomName,
  participantName,
}: Record<string, any>) {
  if (!HARD_CODED_SERVER_URL || !HARD_CODED_TOKEN) {
    return null;
  }

  return {
    serverUrl: HARD_CODED_SERVER_URL,
    roomName,
    participantName,
    participantToken: HARD_CODED_TOKEN,
    sessionId: null,
  };
}

export async function fetchConnectionDetails({
  roomName,
  participantName,
  language = '',
  dialect = '',
  authToken,
  requestAuthToken = false,
  patientId,
  patientName,
  ehrPatientId,
  patientGender,
  providerName,
  encounterId,
  workflowType,
  simultaneous = false,
  isTelephonic = false,
}: Record<string, any> = {}) {
  const finalParticipantName = participantName || `user-${uuidv4()}`;
  const fallbackRoomName = roomName || `opalite-web-${uuidv4()}`;

  let firebaseToken = authToken;
  if (requestAuthToken && !firebaseToken) {
    try {
      firebaseToken = await getFirebaseToken();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        'Failed to retrieve Firebase auth token for LiveKit connection',
        error,
      );
    }
  }

  try {
    const backendDetails = await fetchConnectionDetailsFromBackend({
      backendUrl: DEFAULT_BACKEND_URL,
      roomName,
      participantName: finalParticipantName,
      language,
      dialect,
      authToken: firebaseToken,
      patientId,
      patientName,
      ehrPatientId,
      patientGender,
      providerName,
      encounterId,
      workflowType,
      simultaneous,
      isTelephonic,
    });

    if (backendDetails) {
      return backendDetails;
    }
  } catch (error) {
    if (!roomName && error.status === 404) {
      try {
        return await fetchConnectionDetailsFromBackend({
          backendUrl: DEFAULT_BACKEND_URL,
          roomName: fallbackRoomName,
          participantName: finalParticipantName,
          language,
          dialect,
          authToken: firebaseToken,
        });
      } catch (legacyError) {
        // eslint-disable-next-line no-console
        console.error(
          'Failed to fetch legacy LiveKit token from backend',
          legacyError,
        );
      }
    }
    // eslint-disable-next-line no-console
    console.error('Failed to fetch LiveKit token from backend', error);
  }

  const hardcoded = fetchHardCodedConnection({
    roomName: fallbackRoomName,
    participantName: finalParticipantName,
  });

  if (hardcoded) {
    return hardcoded;
  }

  throw new Error('Unable to resolve LiveKit connection details');
}

/**
 * Fetch LiveKit connection details for a SCRIBE session.
 *
 * The client mints `conversationId` so it can navigate to / generate the note
 * by that id; the opalite-livekit scribe agent writes the Conversation +
 * Messages docs under it. The patient / template / encounter context is sent
 * so the backend can stamp it into room metadata for the agent.
 */
export async function fetchScribeConnectionDetails({
  conversationId,
  participantName,
  patientId,
  patientName,
  ehrPatientId,
  templateType,
  specialty,
  encounterId,
  isTelephonic = false,
  isDictation = false,
  authToken,
  requestAuthToken = true,
}: Record<string, any> = {}) {
  if (!conversationId) {
    throw new Error('conversationId is required for a scribe LiveKit session');
  }

  const finalParticipantName = participantName || `provider-${uuidv4()}`;

  let firebaseToken = authToken;
  if (requestAuthToken && !firebaseToken) {
    try {
      firebaseToken = await getFirebaseToken();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        'Failed to retrieve Firebase auth token for scribe LiveKit connection',
        error,
      );
    }
  }

  const baseUrl = DEFAULT_BACKEND_URL.replace(/\/?$/, '');
  // Unified session API: a scribe session is the /v1/scribe/sessions endpoint
  // with a `web` device; the note/billing context is nested under `context`
  // (conversationId required).
  const response = await request('/v1/scribe/sessions', {
    method: 'POST',
    baseUrl,
    auth: false,
    authToken: firebaseToken,
    unwrap: false,
    successCheck: false,
    body: JSON.stringify({
      deviceKind: 'web',
      participantName: finalParticipantName,
      context: {
        conversationId,
        patientId: patientId || undefined,
        patientName: patientName || undefined,
        ehrPatientId: ehrPatientId || undefined,
        templateType: templateType || undefined,
        specialty: specialty || undefined,
        encounterId: encounterId || undefined,
        // Web dial-out scribe: mark the session telephonic up front so the
        // backend stamps `is_telephonic` into room metadata and the agent
        // records the Conversation as a Phone visit from creation (no window
        // where recents classify it as In-person).
        ...(isTelephonic ? {isTelephonic: true} : {}),
        // Solo provider dictation: the backend records a DictationIntent
        // marker so the session lands with the `dictation` workflow tag (the
        // recording pipeline itself is unchanged scribe).
        ...(isDictation ? {isDictation: true} : {}),
      },
    }),
  });
  const data = unwrapLiveKitResponse(response);
  const token = data.token || data.participantToken;

  if (!token) {
    throw new Error('LiveKit scribe token response missing token field');
  }

  return {
    serverUrl: data.serverUrl || data.server_url || DEFAULT_LIVEKIT_SERVER_URL,
    roomName: data.roomName || data.room_name,
    participantName:
      data.participantName || data.participant_name || finalParticipantName,
    participantToken: token,
  };
}

/**
 * Attach an additional device (e.g. a second screen for the SAME user) to an
 * existing session and get its join token. The backend issues a subscribe-only
 * token for `second_screen`, so a second device follows along without
 * publishing a second mic. Powers the "Add a device" flow (feature A).
 */
export async function attachDeviceToSession({
  roomName,
  participantName,
  kind = 'second_screen',
  language = '',
  dialect = '',
  role,
  authToken,
  requestAuthToken = true,
}: Record<string, any> = {}) {
  if (!roomName) {
    throw new Error('roomName is required to attach a device to a session');
  }

  let firebaseToken = authToken;
  if (requestAuthToken && !firebaseToken) {
    try {
      firebaseToken = await getFirebaseToken();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        'Failed to retrieve Firebase auth token for device attach',
        error,
      );
    }
  }

  const baseUrl = DEFAULT_BACKEND_URL.replace(/\/?$/, '');
  const response = await request(
    `/v1/sessions/${encodeURIComponent(roomName)}/devices`,
    {
      method: 'POST',
      baseUrl,
      auth: false,
      authToken: firebaseToken,
      unwrap: false,
      successCheck: false,
      body: JSON.stringify({
        kind,
        participantName: participantName || `device-${uuidv4()}`,
        language,
        dialect,
        role: role || undefined,
      }),
    },
  );
  const data = unwrapLiveKitResponse(response);
  const token = data.token || data.participantToken;
  if (!token) {
    throw new Error('Device attach response missing token field');
  }

  return {
    serverUrl: data.serverUrl || data.server_url || DEFAULT_LIVEKIT_SERVER_URL,
    roomName: data.roomName || data.room_name || roomName,
    participantName:
      data.participantName || data.participant_name || participantName,
    participantIdentity: data.participantIdentity || data.participant_identity,
    participantToken: token,
  };
}

/**
 * Dial a phone participant into an existing session via the backend (LiveKit
 * SIP). The in-room agent handles the new phone leg; the language/role we send
 * are stamped onto the SIP participant so the agent knows who they are. Powers
 * the "Add a phone person" flow (feature B).
 */
export async function addPhoneToSession({
  roomName,
  phoneNumber,
  language = '',
  dialect = '',
  role,
  displayName,
  callerId,
  authToken,
  requestAuthToken = true,
}: Record<string, any> = {}) {
  if (!roomName) {
    throw new Error('roomName is required to add a phone to a session');
  }
  if (!phoneNumber) {
    throw new Error('phoneNumber is required to add a phone to a session');
  }

  let firebaseToken = authToken;
  if (requestAuthToken && !firebaseToken) {
    try {
      firebaseToken = await getFirebaseToken();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        'Failed to retrieve Firebase auth token for phone add',
        error,
      );
    }
  }

  const baseUrl = DEFAULT_BACKEND_URL.replace(/\/?$/, '');
  const response = await request(
    `/v1/sessions/${encodeURIComponent(roomName)}/phone`,
    {
      method: 'POST',
      baseUrl,
      auth: false,
      authToken: firebaseToken,
      unwrap: false,
      successCheck: false,
      body: JSON.stringify({
        phoneNumber,
        language,
        dialect,
        role: role || undefined,
        displayName: displayName || undefined,
        callerId: callerId || undefined,
      }),
    },
  );
  const data = unwrapLiveKitResponse(response);
  return {
    roomName: data.roomName || data.room_name || roomName,
    participantIdentity: data.participantIdentity || data.participant_identity,
    status: data.status || 'dialing',
  };
}

/**
 * Remove a device/participant (e.g. a dialed-in phone leg) from a session via
 * the backend. Used to hang up the patient's phone without ending the whole
 * encounter. DELETE /v1/sessions/{roomName}/devices/{identity}.
 */
export async function removeDeviceFromSession({
  roomName,
  identity,
  authToken,
  requestAuthToken = true,
}: Record<string, any> = {}) {
  if (!roomName) {
    throw new Error('roomName is required to remove a device from a session');
  }
  if (!identity) {
    throw new Error('identity is required to remove a device from a session');
  }

  let firebaseToken = authToken;
  if (requestAuthToken && !firebaseToken) {
    try {
      firebaseToken = await getFirebaseToken();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        'Failed to retrieve Firebase auth token for device remove',
        error,
      );
    }
  }

  const baseUrl = DEFAULT_BACKEND_URL.replace(/\/?$/, '');
  await request(
    `/v1/sessions/${encodeURIComponent(roomName)}/devices/${encodeURIComponent(identity)}`,
    {
      method: 'DELETE',
      baseUrl,
      auth: false,
      authToken: firebaseToken,
      unwrap: false,
      successCheck: false,
    },
  );
  return {roomName, identity, status: 'removed'};
}

const liveKitTokenService = {
  fetchConnectionDetails,
  fetchScribeConnectionDetails,
  attachDeviceToSession,
  addPhoneToSession,
  removeDeviceFromSession,
};

export default liveKitTokenService;
